import React, { useEffect, useRef } from "react";
import gsap from "gsap";

/**
 * ImageKit optimization utility to explicitly crop and resize image payloads.
 * Serving at 160x160px for an 85x85px container gives crisp 2x Retina clarity
 * while cutting download weight by over 85%.
 */
const getOptimizedLogoUrl = (url) => {
  if (!url) return "";
  if (!url.includes("ik.imagekit.io")) return url;
  const baseUrl = url.split("?")[0];
  return `${baseUrl}?tr=w-160,h-160,fo-auto,q-80,f-auto`;
};

const clientLogos = [
  {
    name: "BAB",
    r: -3,
    img: getOptimizedLogoUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/BAB.PNG"),
  },
  {
    name: "Knight Tech Solutions",
    r: 2,
    img: getOptimizedLogoUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/knight_tech.PNG"),
  },
  {
    name: "Softbeck LLC",
    r: -1,
    img: getOptimizedLogoUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/Softbeck_llc.png"),
  },
  {
    name: "The Blixify",
    r: 4,
    img: getOptimizedLogoUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/theblixify.png"),
  },
  {
    name: "GoldenCoin",
    r: 2,
    img: getOptimizedLogoUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/goldencoin.PNG"),
  },
  {
    name: "GreeksPod",
    r: -4,
    img: getOptimizedLogoUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/greekspod.png"),
  },
  {
    name: "Innovative Consultancy",
    r: 1,
    img: getOptimizedLogoUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/Consultancy.jpg"),
  },
  {
    name: "Softbeck Limited",
    r: -2,
    img: getOptimizedLogoUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/Softbeck.png"),
  },
  {
    name: "Volunteer Force PK",
    r: 3,
    img: getOptimizedLogoUrl("https://ik.imagekit.io/g9mnvfpan/mateen%20/volunteer_force.png"),
  },
];

const ORBIT_SPEED = 25; // seconds for a full 360°

const getConfig = () => {
  const w = window.innerWidth;
  const isMobile = w <= 480;
  const isTablet = w <= 768;
  return {
    orbitRadius: isMobile ? 105 : isTablet ? 145 : w < 1024 ? 210 : 300,
    proximityRadius: isTablet ? 100 : 200,
    maxScale: isMobile ? 1.2 : isTablet ? 1.4 : 2.0,
    duration: 0.35,
    isTablet,
  };
};

export default function ClientLogosProximityGrid() {
  const stageRef = useRef(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    let config = getConfig();

    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray(".cl-card");
      const totalCards = cards.length;
      const orbitTweens = [];

      const startOrbits = () => {
        orbitTweens.forEach((t) => t.kill());
        orbitTweens.length = 0;

        cards.forEach((card, index) => {
          const initialAngle = index * (360 / totalCards);
          const orbitData = { angle: initialAngle };

          orbitTweens.push(
            gsap.to(orbitData, {
              angle: initialAngle + 360,
              duration: ORBIT_SPEED,
              repeat: -1,
              ease: "none",
              onUpdate: () => {
                const radians = (orbitData.angle * Math.PI) / 180;
                const xOffset = Math.cos(radians) * config.orbitRadius;
                const yOffset = Math.sin(radians) * config.orbitRadius;
                const cardRotation = orbitData.angle + 90 + (clientLogos[index].r || 0);

                gsap.set(card, {
                  xPercent: -50,
                  yPercent: -50,
                  x: xOffset,
                  y: yOffset,
                  rotation: cardRotation,
                });
              },
            })
          );
        });
      };

      startOrbits();

      const handleMouseMove = (e) => {
        const mx = e.clientX;
        const my = e.clientY;
        cards.forEach((card) => {
          const rect = card.getBoundingClientRect();
          const distance = Math.hypot(
            mx - (rect.left + rect.width / 2),
            my - (rect.top + rect.height / 2)
          );
          const proximity = gsap.utils.clamp(
            0,
            1,
            gsap.utils.mapRange(0, config.proximityRadius, 1, 0, distance)
          );

          gsap.to(card, {
            scale: 1 + (config.maxScale - 1) * proximity,
            duration: config.duration,
            overwrite: "auto",
            ease: "power2.out",
          });
        });
      };

      const handleMouseLeave = () => {
        cards.forEach((card) => {
          gsap.to(card, {
            scale: 1,
            duration: config.duration * 2,
            overwrite: "auto",
            ease: "power2.out",
          });
        });
      };

      if (!config.isTablet) {
        stage.addEventListener("mousemove", handleMouseMove);
        stage.addEventListener("mouseleave", handleMouseLeave);
      }

      let resizeTimer;
      const handleResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          config = getConfig();
          startOrbits();

          if (!config.isTablet) {
            stage.addEventListener("mousemove", handleMouseMove);
            stage.addEventListener("mouseleave", handleMouseLeave);
          } else {
            stage.removeEventListener("mousemove", handleMouseMove);
            stage.removeEventListener("mouseleave", handleMouseLeave);
          }
        }, 200);
      };

      window.addEventListener("resize", handleResize);

      return () => {
        stage.removeEventListener("mousemove", handleMouseMove);
        stage.removeEventListener("mouseleave", handleMouseLeave);
        window.removeEventListener("resize", handleResize);
        clearTimeout(resizeTimer);
      };
    }, stage);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <style>{`
        .cl-wrapper * { box-sizing: border-box; margin: 0; padding: 0; }
        .cl-wrapper {
          width: 100%;
          min-height: 100vh;
          position: relative;
          background-color: #FFFFFF;
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: clamp(60px, 8vw, 120px) clamp(40px, 6vw, 100px);
          overflow: hidden;
        }
        .cl-gradient-bg {
          position: absolute;
          inset: 0;
          background-image: url("https://ik.imagekit.io/g9mnvfpan/bg-gradient.jpg?tr=w-1600,q-70,f-auto");
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          z-index: 1;
          pointer-events: none;
        }
        .cl-pattern-overlay {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          opacity: 0.15;
          z-index: 2;
          pointer-events: none;
        }
        .cl-header {
          text-align: center;
          width: 100%;
          max-width: clamp(260px, 45vw, 440px);
          position: absolute;
          left: 50%; top: 50%;
          transform: translate(-50%, -50%);
          z-index: 10;
          pointer-events: none;
          font-family: 'epilogue', sans-serif;
        }
        .cl-header-tag {
          font-size: clamp(10px, 1.1vw, 12px);
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #F97316;
          margin-bottom: 8px;
          display: inline-block;
        }
        .cl-header h1 {
          font-size: clamp(1.35rem, 3.2vw, 2.4rem);
          font-weight: 700;
          color: #111111;
          line-height: 1.2;
          margin-bottom: clamp(8px, 1.2vw, 14px);
          letter-spacing: -0.03em;
        }
        .cl-header h1 span { color: #F97316; }
        .cl-header p {
          font-size: clamp(0.78rem, 1.25vw, 0.92rem);
          color: #4A4A4A;
          line-height: 1.5;
        }
        .cl-stage {
          position: relative;
          width: 100%;
          max-width: 1000px;
          height: clamp(440px, 80vh, 800px);
          z-index: 5;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: crosshair;
        }
        .cl-grid {
          position: absolute;
          inset: 0;
          width: 100%; height: 100%;
        }
        .cl-card {
          position: absolute;
          left: 50%; top: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transform-origin: center center;
          pointer-events: none;
          width: clamp(80px, 9vw, 120px);
          height: clamp(80px, 9vw, 120px);
          will-change: transform;
        }
        .cl-card-icon {
          width: clamp(52px, 6.5vw, 85px);
          height: clamp(52px, 6.5vw, 85px);
          border-radius: clamp(12px, 1.6vw, 18px);
          padding: clamp(8px, 1vw, 12px);
          display: flex;
          align-items: center;
          justify-content: center;
          background: #FFFFFF;
          box-shadow: 0 8px 24px rgba(0,0,0,0.04);
        }
        .cl-card-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          border-radius: 4px;
        }
        .cl-card-title {
          font-weight: 600;
          font-size: clamp(9px, 1vw, 11px);
          color: #111111;
          text-align: center;
          white-space: nowrap;
          opacity: 0.85;
          background: rgba(255,255,255,0.7);
          padding: 2px 6px;
          border-radius: 4px;
        }
        @media (max-width: 768px) {
          .cl-card-title { display: none; }
          .cl-header { max-width: 240px; }
        }
        @media (max-width: 480px) {
          .cl-header { max-width: 200px; }
        }
      `}</style>

      <div className="cl-wrapper">
        <div className="cl-gradient-bg" />
        <div className="cl-pattern-overlay" />

        <div ref={stageRef} className="cl-stage">
          <div className="cl-header">
            <span className="cl-header-tag">Our Ecosystem</span>
            <h1>
              Brands We've <span>Scaled</span>
            </h1>
            <p>
              We deploy multi-platform store operations that build long-term value, authority, and
              recurring revenue pipelines.
            </p>
          </div>

          <div className="cl-grid">
            {clientLogos.map((logo, index) => (
              <div key={index} className="cl-card">
                <div className="cl-card-icon">
                  <img
                    src={logo.img}
                    alt={logo.name}
                    width={85}
                    height={85}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <span className="cl-card-title">{logo.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}